"use client";

import { Atom, Beaker, Box, Cpu, Droplet, Flame, Layers, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const materials = [{
    name: "Strategic Consulting",
    description: "Expert guidance to optimize your business operations and drive sustainable growth through data-driven insights.",
    tensile: "Efficiency",
    temp: "Growth",
    Icon: Layers
  }, {
    name: "Digital Transformation",
    description: "Modernizing your legacy systems with cutting-edge cloud solutions to ensure agility and competitive advantage.",
    tensile: "Scalability",
    temp: "Speed",
    Icon: Droplet
  }, {
    name: "Market Analysis",
    description: "Deep-dive research into industry trends to help you identify new opportunities and mitigate potential risks.",
    tensile: "Accuracy",
    temp: "Insight",
    Icon: Beaker
  }, {
    name: "Brand Strategy",
    description: "Crafting compelling narratives and visual identities that resonate with your target audience and build loyalty.",
    tensile: "Identity",
    temp: "Impact",
    Icon: Box
  }, {
    name: "Process Automation",
    description: "Streamlining repetitive tasks with intelligent workflows to free up your team for high-value creative work.",
    tensile: "Precision",
    temp: "Output",
    Icon: Atom
  }, {
    name: "Security Audits",
    description: "Comprehensive assessments to protect your digital assets and ensure compliance with global industry standards.",
    tensile: "Integrity",
    temp: "Safety",
    Icon: Shield
  }, {
    name: "Cloud Infrastructure",
    description: "Building robust and secure cloud environments tailored to support your unique business requirements.",
    tensile: "Reliability",
    temp: "Uptime",
    Icon: Cpu
  }, {
    name: "Change Management",
    description: "Guiding your organization through transitions with structured frameworks to ensure seamless adoption.",
    tensile: "Stability",
    temp: "Culture",
    Icon: Flame
  }];
  return <section data-section-id="2254" id="service-breakdown" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-6" style={{
            color: "var(--chart-1)"
          }}>
              Our Expertise
            </p>
            <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-foreground">
              Solutions for modern enterprises.
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              From strategic planning to technical implementation, our comprehensive service suite 
              empowers organizations to navigate complexity and achieve long-term success in 
              an evolving digital landscape.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {materials.map((material, i) => {
          const Icon = material.Icon;
          return <div data-index={i} key={material.name} className="group relative rounded-lg border border-border bg-card p-7 md:p-8 transition-all duration-300 ease-out hover:-translate-y-1 shadow hover:shadow-lg" style={{
            backgroundColor: "var(--card)"
          }}>
                {}
                <div className="mb-7 inline-flex h-[60px] w-[60px] items-center justify-center rounded-lg transition-colors duration-300" style={{
              backgroundColor: "var(--muted)"
            }}>
                  <Icon className="h-6 w-6 transition-colors duration-300 group-hover:text-background" strokeWidth={1.25} style={{
                color: "var(--chart-1)"
              }} />
                  <span className="absolute pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 h-[60px] w-[60px] rounded-lg -z-0" aria-hidden="true" />
                </div>

                {}
                <style>{`
                  .group:hover [data-badge-fill="${i}"] {
                    background-color: var(--chart-1);
                  }
                `}</style>

                {}
                <h3 className="font-serif font-default text-[28px] leading-tight text-card-foreground mb-3">
                  {material.name}
                </h3>

                {}
                <p className="text-sm leading-relaxed text-muted-foreground mb-8">
                  {material.description}
                </p>

                {}
                <div className="pt-5 border-t border-border">
                  <p className="font-mono text-xs tracking-wider uppercase" style={{
                color: "var(--muted-foreground)"
              }}>
                    <span style={{
                  color: "var(--chart-1)"
                }}>FOCUS:</span>{" "}
                    <span className="text-foreground">{material.tensile}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{
                  color: "var(--chart-1)"
                }}>GOAL:</span>{" "}
                    <span className="text-foreground">{material.temp}</span>
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>

      {}
      <style>{`
        #service-breakdown .group:hover .material-badge-icon {
          color: var(--primary-foreground);
        }
      `}</style>
    </section>;
}